import type { BoardWidget, View } from '@amzn/ad-console-canvas';
import { PANTHEON_SOURCE_NAME, ROW_HEIGHT, WidgetCategory } from './constants';
import { recomputeFullWidthLayout, recomputeLayout } from './placement';

/**
 * Default canvas state used until the agent emits its own widgets, and what
 * `resetCanvas` would seed if we re-enabled it. The IDs and props match what
 * the bundled Pantheon modules expect.
 *
 * Layout is derived from the widget list via `recomputeLayout`, so the seed
 * stays in sync with the placement engine's zone ordering.
 *
 * Returns a fresh object on each call so reducer mutations stay isolated.
 */

const SUMMARY_MARKDOWN = `### Summary

For last 7 days (Apr 19 - Apr 26, 2026), all advertisers, all campaigns in Avery Dennison, 1915301200401

&nbsp;

- Your account generated **$4.44M** in sales (+13.64% vs. prior period) at a ROAS of **3.44**, with **$5.25M** in long-term sales reflecting a **4.06** long-term ROAS.
- Sales are growing faster than cost — a positive efficiency signal. Amazon DSP is your dominant reach channel, delivering **300.87M** impressions (+7.53%) and driving **$4.87M** in long-term sales.
- **AVD_SP_LabelMaterials_Broad_Apr2026** is the standout DSP performer, reaching 12.90M users with $745.18K in long-term sales at a CPM of $3.38.
- Sponsored Products is your conversion engine, with a long-term ROAS of **5.09** indicating strong downstream brand value well above its short-term **3.02**.
- Two SP campaigns (**Campaign C-6349** and **Campaign C-6331**) are spending above **$1.4K** each with ROAS below **1.05** and may need budget reallocation.`;

export const buildSeedWidgets = (): BoardWidget[] => [
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-summary-card`,
    widgetId: 'seed-summary',
    props: {
      envelope: { widgetId: 'seed-summary', contentType: 'markdown', summary: SUMMARY_MARKDOWN },
      category: WidgetCategory.SUMMARY
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-kpi`,
    widgetId: 'seed-kpi',
    props: {
      envelope: {
        widgetId: 'seed-kpi',
        contentType: 'chart',
        visualizationSpec: {
          type: 'metric',
          title: 'Total Sales',
          subtitle: 'Last 7 days',
          fields: [
            {
              columnId: 'sales',
              label: 'Sales',
              role: 'value',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }
            }
          ],
          data: { value: 4440000, trendFigure: 0.1364, trend: 'up' }
        }
      },
      category: WidgetCategory.KPI
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-bar-chart`,
    widgetId: 'seed-bar-chart',
    props: {
      envelope: {
        widgetId: 'seed-bar-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'bar',
          title: 'Top 5 Amazon DSP campaign performers',
          subtitle: 'Last 7 days (Apr 19 - Apr 26, 2026)',
          fields: [
            { columnId: 'campaign', label: 'Campaign', role: 'dimension', dataType: 'string' },
            {
              columnId: 'sales',
              label: 'Long-term sales',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            }
          ],
          data: {
            rows: [
              { campaign: 'AVD_DSP_LabelMaterials_Prospecting_Apr2026', sales: 72000 },
              { campaign: 'AVD_DSP_OfficeSupplies_Retargeting_Apr2026', sales: 58000 },
              { campaign: 'AVD_DSP_IndustrialTape_Contextual_Apr2026', sales: 41000 },
              { campaign: 'AVD_DSP_ShippingLabels_Lookalike_Apr2026', sales: 38000 },
              { campaign: 'AVD_DSP_BrandAwareness_Video_Apr2026', sales: 12000 }
            ]
          }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-line-chart`,
    widgetId: 'seed-line-chart',
    props: {
      envelope: {
        widgetId: 'seed-line-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'line',
          title: 'Daily impressions trend',
          subtitle: 'Apr 19 - Apr 26, 2026',
          fields: [
            { columnId: 'date', label: 'Date', role: 'dimension', dataType: 'date' },
            {
              columnId: 'impressions',
              label: 'Impressions',
              role: 'metric',
              dataType: 'number',
              format: { style: 'decimal', notation: 'compact' }
            },
            { columnId: 'clicks', label: 'Clicks', role: 'metric', dataType: 'number', format: { style: 'decimal', notation: 'compact' } }
          ],
          data: {
            rows: [
              { date: '2026-04-19', impressions: 45000, clicks: 1200 },
              { date: '2026-04-20', impressions: 48000, clicks: 1350 },
              { date: '2026-04-21', impressions: 52000, clicks: 1500 },
              { date: '2026-04-22', impressions: 47000, clicks: 1280 },
              { date: '2026-04-23', impressions: 55000, clicks: 1600 },
              { date: '2026-04-24', impressions: 60000, clicks: 1750 },
              { date: '2026-04-25', impressions: 58000, clicks: 1680 }
            ]
          }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-pie-chart`,
    widgetId: 'seed-pie-chart',
    props: {
      envelope: {
        widgetId: 'seed-pie-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'pie',
          title: 'Spend distribution by ad type',
          subtitle: 'Last 7 days',
          fields: [
            { columnId: 'adType', label: 'Ad Type', role: 'dimension', dataType: 'string' },
            {
              columnId: 'spend',
              label: 'Spend',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            }
          ],
          data: {
            slices: [
              { adType: 'Sponsored Products', spend: 520000 },
              { adType: 'Amazon DSP', spend: 380000 },
              { adType: 'Sponsored Brands', spend: 210000 },
              { adType: 'Sponsored Display', spend: 180000 }
            ]
          }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-table`,
    widgetId: 'seed-table',
    props: {
      envelope: {
        widgetId: 'seed-table',
        contentType: 'chart',
        visualizationSpec: {
          type: 'table',
          title: 'Campaign performance breakdown',
          subtitle: 'Top campaigns by sales',
          fields: [
            { columnId: 'campaign', label: 'Campaign', role: 'dimension', dataType: 'string' },
            {
              columnId: 'impressions',
              label: 'Impressions',
              role: 'metric',
              dataType: 'number',
              format: { style: 'decimal', notation: 'compact' }
            },
            { columnId: 'clicks', label: 'Clicks', role: 'metric', dataType: 'number', format: { style: 'decimal', notation: 'compact' } },
            {
              columnId: 'sales',
              label: 'Sales',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            },
            { columnId: 'roas', label: 'ROAS', role: 'metric', dataType: 'number', format: { style: 'decimal', maximumFractionDigits: 1 } }
          ],
          data: {
            rows: [
              { campaign: 'AVD_SP_LabelMaterials_Broad', impressions: 1200000, clicks: 34000, sales: 745000, roas: 5.2 },
              { campaign: 'AVD_DSP_Prospecting', impressions: 12900000, clicks: 89000, sales: 580000, roas: 3.8 },
              { campaign: 'AVD_SP_OfficeSupplies_Exact', impressions: 890000, clicks: 28000, sales: 410000, roas: 4.9 },
              { campaign: 'AVD_DSP_Retargeting', impressions: 5600000, clicks: 67000, sales: 380000, roas: 4.1 },
              { campaign: 'AVD_SB_BrandVideo', impressions: 3400000, clicks: 45000, sales: 290000, roas: 3.2 },
              { campaign: 'AVD_SD_InMarket_Contextual', impressions: 2100000, clicks: 31000, sales: 210000, roas: 2.8 },
              { campaign: 'AVD_SP_IndustrialTape_Phrase', impressions: 670000, clicks: 19000, sales: 185000, roas: 4.3 }
            ]
          },
          options: { sortBy: 'sales', sortDirection: 'desc' }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-column-chart`,
    widgetId: 'seed-column-chart',
    props: {
      envelope: {
        widgetId: 'seed-column-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'bar',
          title: 'Weekly spend by channel',
          subtitle: 'Apr 19 - Apr 26, 2026',
          fields: [
            { columnId: 'week', label: 'Week', role: 'dimension', dataType: 'string' },
            {
              columnId: 'sp',
              label: 'Sponsored Products',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            },
            {
              columnId: 'dsp',
              label: 'Amazon DSP',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            }
          ],
          data: {
            rows: [
              { week: 'Mon', sp: 74000, dsp: 54000 },
              { week: 'Tue', sp: 78000, dsp: 56000 },
              { week: 'Wed', sp: 82000, dsp: 58000 },
              { week: 'Thu', sp: 71000, dsp: 52000 },
              { week: 'Fri', sp: 85000, dsp: 61000 },
              { week: 'Sat', sp: 68000, dsp: 48000 },
              { week: 'Sun', sp: 62000, dsp: 45000 }
            ]
          },
          options: { stacking: 'normal' }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-waterfall-chart`,
    widgetId: 'seed-waterfall-chart',
    props: {
      envelope: {
        widgetId: 'seed-waterfall-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'waterfall',
          title: 'Sales change attribution',
          subtitle: 'Period-over-period breakdown',
          fields: [
            { columnId: 'factor', label: 'Factor', role: 'dimension', dataType: 'string' },
            {
              columnId: 'impact',
              label: 'Impact',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            }
          ],
          data: {
            rows: [
              { factor: 'Prior period', impact: 3910000, isSum: true },
              { factor: 'SP growth', impact: 280000 },
              { factor: 'DSP growth', impact: 190000 },
              { factor: 'SB growth', impact: 95000 },
              { factor: 'SD decline', impact: -35000 },
              { factor: 'Current period', impact: 4440000, isSum: true }
            ]
          }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-cartesian-chart`,
    widgetId: 'seed-area-chart',
    props: {
      envelope: {
        widgetId: 'seed-area-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'combo',
          title: 'Cumulative spend over time',
          subtitle: 'Apr 19 - Apr 26, 2026',
          fields: [
            { columnId: 'date', label: 'Date', role: 'dimension', dataType: 'date' },
            {
              columnId: 'sp',
              label: 'Sponsored Products',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            },
            {
              columnId: 'dsp',
              label: 'Amazon DSP',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            }
          ],
          data: {
            rows: [
              { date: '2026-04-19', sp: 74000, dsp: 54000 },
              { date: '2026-04-20', sp: 152000, dsp: 110000 },
              { date: '2026-04-21', sp: 234000, dsp: 168000 },
              { date: '2026-04-22', sp: 305000, dsp: 220000 },
              { date: '2026-04-23', sp: 390000, dsp: 281000 },
              { date: '2026-04-24', sp: 458000, dsp: 329000 },
              { date: '2026-04-25', sp: 520000, dsp: 374000 }
            ]
          },
          options: {
            series: [
              { field: 'sp', chartType: 'area', yAxisIndex: 0 },
              { field: 'dsp', chartType: 'area', yAxisIndex: 0 }
            ]
          }
        }
      },
      category: WidgetCategory.CHART
    }
  },
  {
    type: `${PANTHEON_SOURCE_NAME}:canvas-widget-cartesian-chart`,
    widgetId: 'seed-combo-chart',
    props: {
      envelope: {
        widgetId: 'seed-combo-chart',
        contentType: 'chart',
        visualizationSpec: {
          type: 'combo',
          title: 'Spend vs ROAS trend',
          subtitle: 'Apr 19 - Apr 26, 2026',
          fields: [
            { columnId: 'date', label: 'Date', role: 'dimension', dataType: 'date' },
            {
              columnId: 'spend',
              label: 'Spend',
              role: 'metric',
              dataType: 'currency',
              format: { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 0 }
            },
            { columnId: 'roas', label: 'ROAS', role: 'metric', dataType: 'number', format: { style: 'decimal', maximumFractionDigits: 1 } }
          ],
          data: {
            rows: [
              { date: '2026-04-19', spend: 128000, roas: 3.2 },
              { date: '2026-04-21', spend: 140000, roas: 3.5 },
              { date: '2026-04-23', spend: 146000, roas: 3.6 },
              { date: '2026-04-25', spend: 145000, roas: 3.5 }
            ]
          },
          options: {
            yAxes: [
              { label: 'Spend ($)', position: 'left' },
              { label: 'ROAS', position: 'right' }
            ],
            series: [
              { field: 'spend', chartType: 'column', yAxisIndex: 0 },
              { field: 'roas', chartType: 'line', yAxisIndex: 1 }
            ]
          }
        }
      },
      category: WidgetCategory.CHART
    }
  }
];

export const initialAnalysisWorkspaceView = (): View => {
  const widgets: BoardWidget[] = [];
  return {
    version: 1,
    rowHeight: ROW_HEIGHT,
    widgets,
    layout: { lg: recomputeLayout(widgets), md: recomputeFullWidthLayout(widgets, 6), sm: recomputeFullWidthLayout(widgets, 4) }
  };
};
